/**
 *  Generic common file to store types/helpers.
 */
import { useLocation } from 'react-router';
import { AxiosRequestConfig } from 'axios';

/**
 * HTTP methods supported by axios
 */
export type HttpMethod = AxiosRequestConfig['method'];

/**
 * All asset types that may exist.
 * Some we do not support, eg flash.
 */
export type AssetType =
  | 'article'
  | 'image'
  | 'collection'
  | 'audio'
  | 'file'
  | 'flash'
  | 'html'
  | 'link'
  | 'pdf'
  | 'poll'
  | 'table'
  | 'video'
  | 'youtube'
  | 'zip';

export const assetTypesSet: ReadonlySet<AssetType> = new Set([
  'article',
  'image',
  'collection',
  'audio',
  'file',
  'flash',
  'html',
  'link',
  'pdf',
  'poll',
  'table',
  'video',
  'youtube',
  'zip',
]);

export const assetTypesArray: AssetType[] = Array.from(assetTypesSet);

/**
 * Types of assets which we editors built for.
 */

export type EditorAssetType =
  | 'article'
  | 'audio'
  | 'collection'
  | 'file'
  | 'html'
  | 'image'
  | 'link'
  | 'poll'
  | 'table'
  | 'video';

export const editorAssetTypesSet: ReadonlySet<EditorAssetType> = new Set([
  'article',
  'audio',
  'collection',
  'file',
  'html',
  'image',
  'link',
  'poll',
  'table',
  'video',
]);

/**
 * Utility to check if type string is valid for editorial assets.
 * Valid if the string exists in the editorAssetTypesSet set.
 */
export function isEditorAssetType(key: string): key is EditorAssetType {
  if (!key || typeof key !== 'string') return false;
  const normalized = key?.toLowerCase()?.trim() as EditorAssetType;
  return editorAssetTypesSet.has(normalized);
}

/**
 * All asset types that we support in the app which
 * are also enabled for creation or editing.
 */

export type EditorEnabledAssetType =
  | 'article'
  | 'audio'
  | 'collection'
  | 'file'
  | 'html'
  | 'image'
  | 'link'
  | 'poll'
  | 'table'
  | 'video';

export const editorEnabledAssetTypesSet: ReadonlySet<EditorAssetType> = new Set([
  'article',
  'audio',
  'collection',
  'file',
  'html',
  'image',
  'link',
  'poll',
  'table',
  'video',
]);

// A record with string key and any value
export type AnyRecord = Record<string, any>;

// same as AnyRecord but different style
export type KeyStringAny = {
  [key: string]: any;
};

// A record with string key and string value
export type StringRecord = Record<string, string>;

// Wrapper utility types
export type WithAny<T> = T & {
  [key: string]: any;
};

export type WithString<T> = T & {
  [key: string]: string;
};

export type WithStringArray<T> = T & {
  [key: string]: string[];
};

export type ToWeakBoolean<T> = {
  [P in keyof T]: boolean | undefined;
};

export type UseLocationType = ReturnType<typeof useLocation>;
